// hooks/billing/useOrgUsageByAccount.js
import { useCallback, useEffect, useState } from 'react';
import BillingService from '../../services/billing.service';

/**
 * useOrgUsageByAccount — `GET /organizations/{orgId}/billing/usage-by-account`.
 *
 * Refetches when `since` or `until` change. Same Enterprise-only
 * constraint as useOrgBalance.
 *
 * @param {string} organizationId
 * @param {Object} [params]
 * @param {string} [params.since]
 * @param {string} [params.until]
 * @param {boolean} [params.auto=true]
 */
export function useOrgUsageByAccount(
  organizationId,
  { since, until, auto = true } = {}
) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(auto && organizationId));
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    const result = await BillingService.getOrgUsageByAccount(organizationId, {
      since,
      until,
    });
    if (result.success) {
      setData(result.data);
    } else {
      setError(result);
    }
    setLoading(false);
  }, [organizationId, since, until]);

  useEffect(() => {
    if (auto && organizationId) {
      refetch();
    } else if (!organizationId) {
      setData(null);
      setError(null);
      setLoading(false);
    }
  }, [auto, organizationId, refetch]);

  return { data, loading, error, refetch };
}

export default useOrgUsageByAccount;
